// The one language an intake page speaks, in the client's own order of
// say: a choice they made on these pages > the language the session was
// set up in > the browser's list > English. The choice outlives the tab
// (localStorage) because the same person comes back through the same
// browser; a browser that blocks site data simply forgets it.

import { useState } from "react";
import type { IntakeLanguage } from "@duralex/shared";
import { INTAKE_LANGUAGES } from "../lib/intakeLogic";

const KEY = "intakeLocale";

function known(value: unknown): IntakeLanguage | null {
  return typeof value === "string" && (INTAKE_LANGUAGES as string[]).includes(value)
    ? (value as IntakeLanguage)
    : null;
}

export function storedLocale(): IntakeLanguage | null {
  try {
    return known(window.localStorage.getItem(KEY));
  } catch {
    return null;
  }
}

function rememberLocale(locale: IntakeLanguage): void {
  try {
    window.localStorage.setItem(KEY, locale);
  } catch {
    // Nothing to do: the choice lives on in this page's state.
  }
}

/** The first of the browser's languages the questionnaire knows, by its
    primary tag: "ru-RU" is Russian, "pt-BR" is nobody's yet. */
export function browserLocale(): IntakeLanguage | null {
  const list = typeof navigator === "undefined" ? [] : (navigator.languages ?? []);
  for (const tag of list) {
    const hit = known(tag.toLowerCase().split("-")[0]);
    if (hit) return hit;
  }
  return null;
}

/** `session`: the language the session carries, when there is one.
    `spoken`: the languages the questionnaire can be taken in, once the
    server has said - a wish outside that list means English. */
export function useIntakeLocale(
  session: IntakeLanguage | null | undefined,
  spoken: IntakeLanguage[] | undefined,
): { locale: IntakeLanguage; choose: (locale: IntakeLanguage) => void } {
  const [chosen, setChosen] = useState<IntakeLanguage | null>(storedLocale);
  const [fromBrowser] = useState<IntakeLanguage | null>(browserLocale);
  const wanted = chosen ?? session ?? fromBrowser ?? "en";
  const locale = spoken && !spoken.includes(wanted) ? "en" : wanted;
  const choose = (next: IntakeLanguage) => {
    setChosen(next);
    rememberLocale(next);
  };
  return { locale, choose };
}
